import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAgents } from "@/components/Agents/AgentsContext";
import { scheduleCadenceLabel, type OrgAgent } from "@/lib/agentsDemoData";
import { agentsHref } from "@/lib/atwAgentsHost";

export function AgentScheduleSection({ agent }: { agent: OrgAgent }) {
  const { org, may, setAgentSchedules } = useAgents();
  const canEdit = may("manage_agent_configuration");
  const selected = new Set(agent.scheduleIds ?? []);

  const toggle = (scheduleId: string) => {
    if (!canEdit) return;
    const next = new Set(agent.scheduleIds ?? []);
    if (next.has(scheduleId)) next.delete(scheduleId);
    else next.add(scheduleId);
    const saved = setAgentSchedules(agent.id, [...next]);
    if (!saved) {
      toast.error("Could not save schedules for this agent.");
      return;
    }
    toast.success("Schedules saved on this agent.");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Schedule</CardTitle>
        <CardDescription>
          Pick one or more organization schedules for this agent. Schedules are shared. This agent
          does not own them.
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm">
        {org.schedules.length === 0 ? (
          <p className="text-muted-foreground">
            No schedules yet.{" "}
            <Link to={agentsHref("/schedules")} className="underline underline-offset-2">
              Open Schedules
            </Link>{" "}
            to add one for the organization.
          </p>
        ) : (
          <ul className="space-y-2">
            {org.schedules.map((schedule) => (
              <li key={schedule.id}>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected.has(schedule.id)}
                    disabled={!canEdit}
                    onChange={() => toggle(schedule.id)}
                  />
                  <span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{schedule.name}</span>
                    <span className="mt-0.5 block text-gray-600 dark:text-gray-400">
                      {scheduleCadenceLabel(schedule.cadence)}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
